'use client';

import { env } from '@ria-client/env';
import { Skeleton } from '@ria-client/tw-shadcn/components/skeleton';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { getTikTokVideoId } from './tiktok-url';

const OEMBED_URL = 'https://www.tiktok.com/oembed?url=';
const SCRIPT_ID = 'tiktok-embed-script';
const DEFAULT_SCRIPT_SRC = 'https://www.tiktok.com/embed.js';

// embed.js only renders blockquotes present when it runs and has no public render(),
// so every new embed removes and re-appends it. Already-rendered embeds are untouched.
function reloadEmbedScript() {
  document.getElementById(SCRIPT_ID)?.remove();
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = DEFAULT_SCRIPT_SRC;
  document.head.appendChild(script);
}

export function TikTokEmbed({ url }: { url: string }) {
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '400px' });
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  // embed.js swaps the blockquote for an iframe; show the card once that iframe loads.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLIFrameElement) {
            node.addEventListener('load', () => setLoaded(true), { once: true });
          }
        }
      }
    });
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;

    fetch(OEMBED_URL + encodeURIComponent(url))
      .then(response => {
        if (!response.ok) throw new Error(`TikTok oEmbed responded ${response.status}`);
        return response.json() as Promise<{ html?: string }>;
      })
      .then(({ html }) => {
        if (cancelled) return;
        if (typeof html !== 'string' || !html)
          throw new Error('TikTok oEmbed response has no embed');
        const videoId = getTikTokVideoId(url);
        if (!videoId || !containerRef.current)
          throw new Error('TikTok oEmbed response has no embed');
        // Never inject third-party HTML: embed.js only needs cite and data-video-id.
        const blockquote = document.createElement('blockquote');
        blockquote.className = 'tiktok-embed';
        blockquote.cite = url;
        blockquote.dataset.videoId = videoId;
        // Same bounds as TikTok's oEmbed markup; embed.js sizes the iframe from them.
        blockquote.style.maxWidth = '605px';
        blockquote.style.minWidth = '325px';
        blockquote.append(document.createElement('section'));
        containerRef.current.replaceChildren(blockquote);
        reloadEmbedScript();
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (env.NEXT_PUBLIC_DEPLOYABLE_ENV !== 'prod') {
          console.error('An error occurred in TikTok embed: ', error);
        }
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [inView, url]);

  if (failed) return null;

  return (
    <div ref={ref}>
      {!loaded && <Skeleton className="mx-auto h-[757px] w-full min-w-[325px] max-w-[605px]" />}
      {/* Center the card and remove vertical margin to avoid layout shift; TikTok's CSS is unlayered, so utilities need !. */}
      <div
        className={loaded ? '[&_blockquote]:mx-auto! [&_blockquote]:my-0!' : 'hidden'}
        ref={containerRef}
      />
    </div>
  );
}
